import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { appConfig } from '../config/AppConfig';

const DashboardContext = createContext();

const SYNC_INTERVAL = 300000; // 5 minutes (reduced to avoid slow API blocking UI)

export function DashboardProvider({ children }) {
  const [stats, setStats] = useState(null);
  const [charts, setCharts] = useState(null);
  const [summaryKpis, setSummaryKpis] = useState([]);
  const [peakHours, setPeakHours] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [isSyncing, setIsSyncing] = useState(false);
  const [isRebuilding, setIsRebuilding] = useState(false);
  const [isIncrementalRefreshing, setIsIncrementalRefreshing] = useState(false);
  const [rebuildStatus, setRebuildStatus] = useState(null);
  const [summaryTablesAvailable, setSummaryTablesAvailable] = useState(false);
  const [appliedFilters, setAppliedFilters] = useState(null);
  const [scheduledJobs, setScheduledJobs] = useState(null);
  const [refreshOptions, setRefreshOptions] = useState(null);
  const syncIntervalRef = useRef(null);
  const isInitialLoadRef = useRef(true);

  // Store current filter settings for syncing
  const currentFiltersRef = useRef({
    datePreset: 'all',
    startDate: null,
    endDate: null,
    topN: 10,
    language: 'ar'
  });

  const fetchDashboardData = useCallback(async (isBackground = false, filters = null) => {
    // Don't show loading spinner for background syncs
    if (!isBackground) {
      setLoading(true);
    } else {
      setIsSyncing(true);
    }

    // Use provided filters or current filters ref
    const filterParams = filters || currentFiltersRef.current;

    try {
      await appConfig.load();
      const apiUrl = appConfig.getMainApiUrl();

      // Build URL with filter parameters
      const params = new URLSearchParams({
        db_id: 'kfc_postgres'
      });

      if (filterParams.datePreset && filterParams.datePreset !== 'all') {
        params.append('date_preset', filterParams.datePreset);
      }

      if (filterParams.datePreset === 'custom' && filterParams.startDate && filterParams.endDate) {
        params.append('start_date', filterParams.startDate);
        params.append('end_date', filterParams.endDate);
      }

      if (filterParams.topN) {
        params.append('top_n', filterParams.topN.toString());
      }

      if (filterParams.language) {
        params.append('lang', filterParams.language);
      }

      const response = await fetch(`${apiUrl}/dashboard/charts?${params}`);

      if (!response.ok) {
        throw new Error(`API request failed: ${response.status}`);
      }

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Failed to load dashboard data');
      }

      // Extract charts if available
      if (data.charts) {
        setCharts(data.charts);
      }

      // Extract summary KPIs and peak hours
      if (data.summary_kpis) {
        setSummaryKpis(data.summary_kpis);
      }
      if (data.peak_hours) {
        setPeakHours(data.peak_hours);
      }

      // Transform API response to component format
      const apiStats = data;
      const lookupData = apiStats.lookup_tables?.lookupTables || [];
      const nullFieldData = apiStats.null_field_stats?.nullable_fields || [];
      const vehicleData = apiStats.vehicle_distribution?.data || [];
      const paymentData = apiStats.payment_methods?.data || [];
      const directionData = apiStats.direction_split?.data || [];

      // Transform lookup tables to component format
      const transformedLookups = lookupData.map((table) => ({
        name: table.name,
        source: table.original_excel_column,
        count: table.total_values,
        values: table.mappings.slice(0, 8).map((mapping, idx) => ({
          id: idx + 1,
          text: mapping.text,
          count: mapping.count
        })),
        total: table.mappings.length > 8 ? `${table.total_values} values (showing first 8)` : undefined,
        nullable: table.nullable || false
      }));

      // Calculate data generation distributions from actual data
      const vehicleDistributions = vehicleData.slice(0, 5).map(v =>
        `${v.percentage}% ${v.vehicle_type}`
      );
      if (vehicleData.length > 5) {
        const remainingPct = vehicleData.slice(5).reduce((sum, v) => sum + parseFloat(v.percentage), 0).toFixed(1);
        vehicleDistributions.push(`Remaining ${remainingPct}% spread across ${vehicleData.length - 5} other types`);
      }

      const paymentDistributions = paymentData.slice(0, 5).map(p =>
        `${p.percentage}% ${p.payment_method}`
      );
      if (paymentData.length > 5) {
        const remainingPct = paymentData.slice(5).reduce((sum, p) => sum + parseFloat(p.percentage), 0).toFixed(1);
        paymentDistributions.push(`Remaining ${remainingPct}% spread across ${paymentData.length - 5} other methods`);
      }

      const directionBreakdown = directionData.map(d =>
        `${d.percentage}% ${d.direction}`
      );
      directionBreakdown.push('Plaza derived from direction');
      directionBreakdown.push('Lane distribution: 1-10 random, lanes 8-9 for trucks');

      setStats({
        // Core Statistics
        totalTransactions: apiStats.total_transactions || 0,
        vehicleTypes: apiStats.vehicle_distribution?.total_types || 0,
        paymentMethods: apiStats.payment_methods?.total_methods || 0,
        dateRange: {
          min: apiStats.date_range?.start_date || 'N/A',
          max: apiStats.date_range?.end_date || 'N/A',
          totalDays: apiStats.date_range?.total_days || 0
        },

        // Lookup Tables
        lookupTables: transformedLookups,

        // NULL Field Policy
        nullableFields: nullFieldData,

        // Data Generation Strategy
        dataGeneration: {
          strategy: 'Weighted Realistic Distribution',
          coverage: 'ALL lookup values covered',
          distributions: [
            {
              name: 'Vehicle Types',
              icon: 'Truck',
              description: `Realistic distribution covering all ${apiStats.vehicle_distribution?.total_types || 0} vehicle types`,
              breakdown: vehicleDistributions
            },
            {
              name: 'Payment Methods',
              icon: 'CreditCard',
              description: `Realistic distribution covering all ${apiStats.payment_methods?.total_methods || 0} payment methods`,
              breakdown: paymentDistributions
            },
            {
              name: 'Direction & Location',
              icon: 'MapPin',
              description: 'Location-aware assignment',
              breakdown: directionBreakdown
            },
            {
              name: 'Temporal Distribution',
              icon: 'Calendar',
              description: 'Realistic traveler behavior simulation',
              breakdown: [
                'Morning rush: 6 AM - 9 AM',
                'Evening rush: 4 PM - 8 PM',
                'Weekend patterns differ from weekdays',
                'Month: 1-12 for seasonal patterns',
                'Shift based on time of day'
              ]
            }
          ]
        },

        // Schema Overview
        schema: {
          totalColumns: apiStats.schema_stats?.total_columns || 0,
          categories: apiStats.schema_stats?.categories || []
        }
      });

      // Update summary tables status from API response
      setSummaryTablesAvailable(apiStats.summary_tables_available || false);
      if (apiStats.last_rebuild) {
        setRebuildStatus(apiStats.last_rebuild);
      }

      // Store applied filters from API response
      if (apiStats.applied_filters) {
        setAppliedFilters(apiStats.applied_filters);
      }

      setLastUpdated(new Date());
      setError(null);
      isInitialLoadRef.current = false;

    } catch (err) {
      console.error('Dashboard data fetch error:', err);
      // Only set error if this is initial load, not background sync
      if (!isBackground) {
        setError(err.message);
      }
    } finally {
      setLoading(false);
      setIsSyncing(false);
    }
  }, []);

  // Fetch with specific filters (used when user changes settings)
  const fetchWithFilters = useCallback(async (filters) => {
    // Update the ref so background syncs use the same filters
    currentFiltersRef.current = {
      ...currentFiltersRef.current,
      ...filters
    };
    // Fetch with new filters
    await fetchDashboardData(false, currentFiltersRef.current);
  }, [fetchDashboardData]);

  // Initial load on app start
  useEffect(() => {
    fetchDashboardData(false);
  }, [fetchDashboardData]);

  // Background sync every minute
  useEffect(() => {
    syncIntervalRef.current = setInterval(() => {
      fetchDashboardData(true);
    }, SYNC_INTERVAL);

    return () => {
      if (syncIntervalRef.current) {
        clearInterval(syncIntervalRef.current);
      }
    };
  }, [fetchDashboardData]);

  const refresh = useCallback(() => {
    fetchDashboardData(false);
  }, [fetchDashboardData]);

  // Trigger full rebuild of summary tables
  const triggerRebuild = useCallback(async () => {
    setIsRebuilding(true);
    try {
      await appConfig.load();
      const apiUrl = appConfig.getMainApiUrl();
      const response = await fetch(`${apiUrl}/dashboard/rebuild?triggered_by=manual`, {
        method: 'POST'
      });

      if (!response.ok) {
        throw new Error(`Rebuild request failed: ${response.status}`);
      }

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.error || 'Failed to rebuild summary tables');
      }

      // Refresh dashboard data after successful rebuild
      await fetchDashboardData(false);

      return { success: true, message: 'Summary tables rebuilt successfully' };
    } catch (err) {
      console.error('Rebuild error:', err);
      return { success: false, error: err.message };
    } finally {
      setIsRebuilding(false);
    }
  }, [fetchDashboardData]);

  // Trigger incremental refresh (~5 seconds)
  const triggerIncrementalRefresh = useCallback(async () => {
    setIsIncrementalRefreshing(true);
    try {
      await appConfig.load();
      const apiUrl = appConfig.getMainApiUrl();
      const response = await fetch(`${apiUrl}/dashboard/refresh-incremental`, {
        method: 'POST'
      });

      if (!response.ok) {
        throw new Error(`Incremental refresh failed: ${response.status}`);
      }

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.error || 'Failed to run incremental refresh');
      }

      // Refresh dashboard data after successful refresh
      await fetchDashboardData(false);
      // Also refresh the scheduled jobs info
      await fetchScheduledJobs();

      return { success: true, message: 'Incremental refresh completed', duration_ms: result.duration_ms };
    } catch (err) {
      console.error('Incremental refresh error:', err);
      return { success: false, error: err.message };
    } finally {
      setIsIncrementalRefreshing(false);
    }
  }, [fetchDashboardData]);

  // Fetch scheduled jobs and refresh options
  const fetchScheduledJobs = useCallback(async () => {
    try {
      await appConfig.load();
      const apiUrl = appConfig.getMainApiUrl();

      // Fetch both scheduled jobs and refresh options in parallel
      const [jobsResponse, optionsResponse] = await Promise.all([
        fetch(`${apiUrl}/dashboard/scheduled-jobs`),
        fetch(`${apiUrl}/dashboard/refresh-options`)
      ]);

      if (jobsResponse.ok) {
        const jobsData = await jobsResponse.json();
        setScheduledJobs(jobsData.jobs || null);
      }

      if (optionsResponse.ok) {
        const optionsData = await optionsResponse.json();
        setRefreshOptions(optionsData);
      }
    } catch (err) {
      console.error('Failed to fetch scheduled jobs:', err);
    }
  }, []);

  // Fetch scheduled jobs on mount
  useEffect(() => {
    fetchScheduledJobs();
  }, [fetchScheduledJobs]);

  const value = {
    stats,
    charts,
    summaryKpis,
    peakHours,
    loading,
    error,
    lastUpdated,
    isSyncing,
    isInitialLoad: isInitialLoadRef.current,
    refresh,
    // Filter functionality
    fetchWithFilters,
    appliedFilters,
    // Rebuild functionality
    isRebuilding,
    rebuildStatus,
    summaryTablesAvailable,
    triggerRebuild,
    // Incremental refresh functionality
    isIncrementalRefreshing,
    triggerIncrementalRefresh,
    scheduledJobs,
    refreshOptions,
    fetchScheduledJobs
  };

  return (
    <DashboardContext.Provider value={value}>
      {children}
    </DashboardContext.Provider>
  );
}

export function useDashboard() {
  const context = useContext(DashboardContext);
  if (!context) {
    throw new Error('useDashboard must be used within a DashboardProvider');
  }
  return context;
}

export default useDashboard;
